import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from 'react-router'
import {
  chatbubbles,
  chatbubblesOutline,
  home,
  homeOutline,
  informationCircle,
  informationCircleOutline,
  settings,
  settingsOutline,
  logIn,
  logInOutline,
  locationOutline,
  checkmarkOutline,
  wallet,
  walletOutline,
  people,
  peopleOutline,
  star,
  starOutline,
  ribbon,
  ribbonOutline,
  grid,
  gridOutline,
  trophy,
  trophyOutline,
  bagHandle,
  bagHandleOutline,
  newspaperOutline,
  contrastOutline,
  sunnyOutline,
  moonOutline
} from 'ionicons/icons'
import {
  Badge,
  Header,
  Icon,
  OverlayHost,
  Segment,
  SegmentButton,
  Toolbar,
  menuController,
  useIsDesktopNav,
  useMenu
} from '@/ui'
import { useAppRouter, useRoute } from '@/router/navigation'
import { useAuthStore } from '@/stores/auth'
import { useCityStore } from '@/stores/city'
import { useGameStore } from '@/stores/game'
import { useLeaguePortalsStore } from '@/stores/leaguePortals'
import { useThemeStore, type ThemePreference } from '@/stores/theme'
import { useAnalyticsStore } from '@/stores/analytics'
import { useNewslettersStore } from '@/stores/newsletters'
import { usePersonalizationStore } from '@/stores/personalization'
import { useAdminInboxStore } from '@/stores/adminInbox'
import { useNotificationStore } from '@/stores/notification'
import { useChatStore } from '@/stores/chat'
import { SkatePaymentFollowUpModal } from '@/components/SkatePaymentFollowUpModal'
import { NewsletterArchiveModal } from '@/components/NewsletterArchiveModal'
import { PersonalizationChecklistModal } from '@/components/PersonalizationChecklistModal'
import { AdminActionInboxModal } from '@/components/AdminActionInboxModal'
import { AppDownloadPromptModal } from '@/components/AppDownloadPromptModal'
import { NavigationProgress } from '@/components/RouteStatus'
import { SOUTH_SHORE_CITY_ID } from '@/utils/cityIds'
import {
  dismissAppDownloadPromptForever,
  isAppDownloadPromptDismissed,
  isMobileWebViewport,
  isOpenedFromApp
} from '@/utils/appDownloadPrompt'
import './App.css'

const AUTH_ROUTE_NAMES = new Set([
  'Login',
  'Register',
  'ForgotPassword',
  'ResetPassword'
])

/**
 * The half-filled contrast glyph is the established convention for "follow the
 * system", the way a sun and a moon are for light and dark.
 */
const appearanceOptions: Array<{ value: ThemePreference; label: string; icon: string }> = [
  { value: 'system', label: 'Match device', icon: contrastOutline },
  { value: 'light', label: 'Light', icon: sunnyOutline },
  { value: 'dark', label: 'Dark', icon: moonOutline }
]

/* ---------------------------------------------------------------------------
   Side menu: an overlay drawer below 1024px, a persistent sidebar above it.
   Rows are real links so middle-click / open-in-new-tab work.
   ------------------------------------------------------------------------ */

/**
 * Outline -> filled variant for menu rows. The current row shows the filled
 * glyph (the iOS tab-bar convention); any icon not listed stays outline.
 */
const FILLED_ICONS: Record<string, string> = {
  [homeOutline]: home,
  [chatbubblesOutline]: chatbubbles,
  [gridOutline]: grid,
  [informationCircleOutline]: informationCircle,
  [bagHandleOutline]: bagHandle,
  [ribbonOutline]: ribbon,
  [walletOutline]: wallet,
  [settingsOutline]: settings,
  [starOutline]: star,
  [peopleOutline]: people,
  [trophyOutline]: trophy,
  [logInOutline]: logIn
}

/** A left click with no modifier: the only click the app handles itself. */
const isPlainClick = (e: MouseEvent<HTMLAnchorElement>) =>
  !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey

function MenuRowInner ({ icon, end, children }: { icon: string; end?: ReactNode; children: ReactNode }) {
  return (
    <>
      <span className="item__start"><Icon icon={icon} /></span>
      <span className="item__inner">
        <span className="label label--wrap">{children}</span>
        {end ? <span className="item__end">{end}</span> : null}
      </span>
    </>
  )
}

/**
 * A menu row that navigates. NavLink marks the current route with
 * aria-current="page", and that row swaps to its filled icon. `onPlainClick` lets a row run its own async step
 * (switch city, clear city) before navigating; modified clicks still open the
 * href natively.
 */
function MenuLink ({
  to,
  icon,
  end,
  exact,
  className,
  onPlainClick,
  children
}: {
  to: string
  icon: string
  end?: ReactNode
  /** Match the path exactly (Home), rather than as a prefix. */
  exact?: boolean
  className?: string
  onPlainClick?: () => void
  children: ReactNode
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={exact}
        className={`item item--button item--lines-full${className ? ` ${className}` : ''}`}
        onClick={(e) => {
          if (!isPlainClick(e)) return
          if (onPlainClick) {
            e.preventDefault()
            onPlainClick()
          } else {
            // Also covers a tap on the page you're already on.
            void menuController.close()
          }
        }}
      >
        {({ isActive }) => (
          <MenuRowInner icon={isActive ? FILLED_ICONS[icon] ?? icon : icon} end={end}>{children}</MenuRowInner>
        )}
      </NavLink>
    </li>
  )
}

function AppMenu ({ onOpenNewsletterArchive }: { onOpenNewsletterArchive: () => void }) {
  const router = useAppRouter()
  const route = useRoute()
  const location = useLocation()
  const open = useMenu((s) => s.open)
  const isDesktop = useIsDesktopNav()
  const drawerOpen = open && !isDesktop
  const asideRef = useRef<HTMLElement>(null)

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin)
  const hasLeagueAccess = useAuthStore((s) => s.hasLeagueAccess)
  // Re-render on profile changes; the role/payment helpers below read it.
  useAuthStore((s) => s.userProfile)
  const authStore = useAuthStore.getState()

  const currentCity = useCityStore((s) => s.currentCity)
  const currentCityDisplayName = useCityStore((s) => s.currentCityDisplayName)
  const activeCities = useCityStore((s) => s.activeCities)
  const storeCurrentCityId = useCityStore((s) => s.currentCityId)

  const leaguePortals = useLeaguePortalsStore((s) => s.visiblePortals)

  const themePreference = useThemeStore((s) => s.preference)
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)

  // Tell MenuButtons there is a menu to open (none on embeds / the error page).
  useEffect(() => {
    useMenu.setState({ available: true })
    return () => useMenu.setState({ available: false, open: false, badge: null })
  }, [])

  // Close the drawer after any navigation, and when the window grows into
  // the sidebar layout (so it isn't still "open" on the way back down).
  useEffect(() => {
    void menuController.close()
  }, [location.key, isDesktop])

  // While the drawer is open: Escape closes it, the page behind can't scroll,
  // focus moves into the menu and returns to the opener on close.
  useEffect(() => {
    if (!drawerOpen) return
    const opener = document.activeElement as HTMLElement | null
    asideRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void menuController.close()
    }
    window.addEventListener('keydown', onKey)
    document.documentElement.classList.add('menu-drawer-open')
    return () => {
      window.removeEventListener('keydown', onKey)
      document.documentElement.classList.remove('menu-drawer-open')
      if (opener?.isConnected && asideRef.current?.contains(document.activeElement)) opener.focus()
    }
  }, [drawerOpen])

  // Get current city from route or store
  const currentCityId = route.params.cityId || storeCurrentCityId

  // Compute city-scoped paths. About, Payments and City Admin only exist
  // under a city, so those rows are hidden until one is picked.
  const cityHomePath = currentCityId ? `/${currentCityId}` : '/'
  const aboutPath = `/${currentCityId}/about`
  const sponsorsPath = currentCityId ? `/${currentCityId}/sponsors` : '/sponsors'
  const storePath = String(route.params.leagueId || '').trim()
    ? `/leagues/${route.params.leagueId}/store`
    : currentCityId ? `/${currentCityId}/store` : '/store'
  const adminPath = `/${currentCityId}/admin`
  const paymentsPath = `/${currentCityId}/payments`
  const chatsPath = `/${currentCityId}/chats`

  // Get count of pending payments (due or pending)
  const pendingPaymentsCount = authStore.getPaymentsDue().length
  const unreadChatsCount = useChatStore((s) => s.unreadCount)

  // Mirror the Payments and Chats row badges onto the hamburger, which is all a
  // phone sees until the drawer opens. Same condition as the rows being shown.
  const menuPayments = isAuthenticated && currentCityId ? pendingPaymentsCount : 0
  const menuChats = isAuthenticated && currentCityId ? unreadChatsCount : 0
  useEffect(() => {
    const paymentsLabel = menuPayments === 1 ? 'payment due' : 'payments due'
    const chatsLabel = `${menuChats === 1 ? 'chat' : 'chats'} with new messages`
    // The button reads "Open menu, {count} {label}".
    const label = menuPayments > 0 && menuChats > 0
      ? `updates (${menuPayments} ${paymentsLabel}, ${menuChats} ${chatsLabel})`
      : menuPayments > 0 ? paymentsLabel : chatsLabel
    const count = menuPayments + menuChats
    useMenu.setState({ badge: count > 0 ? { count, label } : null })
  }, [menuPayments, menuChats])

  // Check if current user is admin for current city
  const isCurrentCityAdmin = !!currentCityId && authStore.isCityAdmin(currentCityId)

  /** Names the current choice in words, since the icons alone are terse. */
  const appearanceCaption =
    themePreference === 'system'
      ? `Matching your device — ${resolvedTheme} right now`
      : themePreference === 'dark' ? 'Always dark' : 'Always light'

  /** Pickup home landing with all city + league cards */
  const navigateToLanding = async () => {
    await menuController.close()
    useCityStore.getState().clearCurrentCity()
    router.push('/')
  }

  const switchCity = async (cityId: string) => {
    await menuController.close()
    await useCityStore.getState().setCurrentCity(cityId)
    // Track city selection in analytics
    useAnalyticsStore.getState().trackCitySelect(cityId)
    router.push(`/${cityId}`)
  }

  return (
    <>
      {drawerOpen && <div className="menu-backdrop" onClick={() => void menuController.close()} />}
      <aside
        ref={asideRef}
        id="app-menu"
        className={`menu c-app-menu${open ? ' menu--open' : ''}`}
        aria-label="Main menu"
        aria-hidden={isDesktop ? undefined : !open}
        inert={!isDesktop && !open}
      >
        <Header>
          <Toolbar>
            {/* Not a heading: the page owns the h1, and on desktop this is always on screen. */}
            <span className="toolbar-title">{currentCity ? currentCityDisplayName : 'Nova Adult Hockey'}</span>
          </Toolbar>
        </Header>
        <div className="content menu-content">
          <nav className="menu-scroll" aria-label="Main navigation">
            <div className="menu-group">
              <ul className="list" role="list">
                <MenuLink to={cityHomePath} exact icon={homeOutline}>Home</MenuLink>

                {route.name !== 'CitySelect' && (
                  <MenuLink to="/" exact icon={gridOutline} onPlainClick={() => void navigateToLanding()}>
                    All Cities &amp; Leagues
                  </MenuLink>
                )}

                {isAuthenticated && currentCityId && (
                  <MenuLink
                    to={chatsPath}
                    icon={chatbubblesOutline}
                    end={unreadChatsCount > 0
                      ? <Badge color="primary"><span aria-hidden="true">{unreadChatsCount}</span><span className="sr-only">{unreadChatsCount === 1 ? 'chat' : 'chats'} with new messages</span></Badge>
                      : undefined}
                  >
                    Chats
                  </MenuLink>
                )}

                {currentCityId && (
                  <MenuLink to={aboutPath} icon={informationCircleOutline}>About</MenuLink>
                )}

                {/* Opens the archive in place; there is no newsletter page to link to. */}
                <li>
                  <button type="button" className="item item--button item--lines-full" onClick={onOpenNewsletterArchive}>
                    <MenuRowInner icon={newspaperOutline}>Newsletter</MenuRowInner>
                  </button>
                </li>

                <MenuLink to={storePath} icon={bagHandleOutline}>Online Store</MenuLink>

                <MenuLink to={sponsorsPath} icon={ribbonOutline}>Sponsors</MenuLink>

                {isAuthenticated && currentCityId && (
                  <MenuLink
                    to={paymentsPath}
                    icon={walletOutline}
                    end={pendingPaymentsCount > 0 ? <Badge color="warning">{pendingPaymentsCount}</Badge> : undefined}
                  >
                    Payments
                  </MenuLink>
                )}

                {isCurrentCityAdmin && (
                  <MenuLink to={adminPath} icon={settingsOutline}>City Admin</MenuLink>
                )}

                {isSuperAdmin && (
                  <MenuLink to="/super-admin" icon={starOutline}>Super Admin</MenuLink>
                )}

                {hasLeagueAccess && (
                  <MenuLink to="/league" icon={peopleOutline}>My Teams</MenuLink>
                )}

                <MenuLink to="/tournaments" icon={trophyOutline}>Tournaments</MenuLink>
                <MenuLink to="/training" icon={ribbonOutline}>Training</MenuLink>

                {!isAuthenticated && (
                  <MenuLink to="/login" icon={logInOutline}>Login</MenuLink>
                )}
              </ul>
            </div>

            {/* City Switcher Section */}
            {activeCities.length > 1 && (
              <div className="menu-group">
                <span className="eyebrow menu-group__label" id="menu-cities-label">{currentCityId ? 'Switch city' : 'Pickup cities'}</span>
                <ul className="list" role="list" aria-labelledby="menu-cities-label">
                  {activeCities.map((city) => {
                    const isCurrent = city.id === storeCurrentCityId
                    return (
                      <li key={city.id}>
                        <Link
                          to={`/${city.id}`}
                          className={`item item--button item--lines-full${isCurrent ? ' menu-item--current' : ''}`}
                          aria-current={isCurrent ? 'true' : undefined}
                          onClick={(e) => {
                            if (!isPlainClick(e)) return
                            e.preventDefault()
                            void switchCity(city.id)
                          }}
                        >
                          <MenuRowInner
                            icon={locationOutline}
                            end={isCurrent ? <Icon icon={checkmarkOutline} /> : undefined}
                          >
                            {city.id === SOUTH_SHORE_CITY_ID ? 'South Shore' : city.name}
                          </MenuRowInner>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            {/* Leagues (public, no login required) */}
            <div className="menu-group">
              <span className="eyebrow menu-group__label" id="menu-leagues-label">Leagues</span>
              <ul className="list" role="list" aria-labelledby="menu-leagues-label">
                {leaguePortals.map((portal) => (
                  <PortalItems
                    key={portal.id}
                    portal={portal}
                    isAdmin={authStore.isLeagueAdminOf(portal.id)}
                  />
                ))}
              </ul>
            </div>

            {/* Appearance: three-way, because "System" is the default and a
                single on/off switch cannot express it.

                Icon-only. Three word labels did not fit the 288px menu and were
                being clipped mid-word ("ystem"). Each button keeps an
                aria-label, and the caption below names the current choice in
                words so the half-filled "system" glyph is never the only clue. */}
            <div className="menu-group">
              <span className="eyebrow menu-group__label" id="appearance-label">
                Appearance
              </span>
              <Segment
                className="appearance-segment"
                value={themePreference}
                ariaLabelledby="appearance-label"
                onChange={(v: ThemePreference) => useThemeStore.getState().setTheme(v)}
              >
                {appearanceOptions.map((option) => (
                  <SegmentButton key={option.value} value={option.value} ariaLabel={option.label} title={option.label}>
                    <Icon icon={option.icon} />
                  </SegmentButton>
                ))}
              </Segment>
              <p className="appearance-caption">{appearanceCaption}</p>
            </div>
          </nav>
        </div>
      </aside>
    </>
  )
}

function PortalItems ({ portal, isAdmin }: { portal: any; isAdmin: boolean }) {
  return (
    <>
      <MenuLink to={`/leagues/${portal.id}/home`} icon={trophyOutline}>{portal.displayName}</MenuLink>
      {isAdmin && (
        <MenuLink to={`/leagues/${portal.id}/admin`} icon={settingsOutline} className="menu-item--sub">
          Manage {portal.displayName}
        </MenuLink>
      )}
    </>
  )
}

/** Skip link target: the routed page's content, else the whole main column. */
function skipToContent (e: MouseEvent<HTMLAnchorElement>) {
  const target =
    document.querySelector<HTMLElement>('.app-main > .page > .content') ||
    document.getElementById('main-content')
  if (!target) return
  e.preventDefault()
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
  target.focus({ preventScroll: true })
  target.scrollIntoView({ block: 'start' })
}

/* ---------------------------------------------------------------------------
   App shell
   ------------------------------------------------------------------------ */

export default function App () {
  const route = useRoute()
  const location = useLocation()
  const isEmbedRoute = route.meta.embed === true

  // Latest route name for the sync helpers (legacy read `route.name` live).
  const routeNameRef = useRef(route.name)
  routeNameRef.current = route.name
  const isEmbedRef = useRef(isEmbedRoute)
  isEmbedRef.current = isEmbedRoute

  const authLoading = useAuthStore((s) => s.loading)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userUid = useAuthStore((s) => s.user?.uid)
  const userProfileId = useAuthStore((s) => s.userProfile?.id)
  const skatePaymentFollowUps = useAuthStore((s) => s.userProfile?.skatePaymentFollowUps)
  const hubPersonalization = useAuthStore((s) => s.userProfile?.hubPersonalization)

  const checklistOpen = usePersonalizationStore((s) => s.checklistOpen)
  const needsPrompt = usePersonalizationStore((s) => s.needsPrompt)
  const inboxModalOpen = useAdminInboxStore((s) => s.modalOpen)
  const currentCityId = useCityStore((s) => s.currentCityId)
  const chatCityId = route.params.cityId || currentCityId
  const isChatModerator = useAuthStore((s) => Boolean(chatCityId) && (s.isSuperAdmin || s.isCityAdmin(chatCityId)))
  const isDesktopNav = useIsDesktopNav()
  const menuDrawerOpen = useMenu((s) => s.open) && !isDesktopNav && !isEmbedRoute

  // Re-render when published issues change.
  useNewslettersStore((s) => s.publishedIssues)
  const menuNewsletterIssues = useNewslettersStore.getState().issuesForHub(null)

  const [showSkatePaymentFollowUp, setShowSkatePaymentFollowUpState] = useState(false)
  const [newsletterArchiveOpen, setNewsletterArchiveOpen] = useState(false)
  const [activeSkatePaymentFollowUp, setActiveSkatePaymentFollowUp] = useState<any>(null)
  const [showAppDownloadPrompt, setShowAppDownloadPrompt] = useState(false)

  // The sync helpers read this synchronously after setting it (Vue ref semantics).
  const showSkateRef = useRef(false)
  const setShowSkatePaymentFollowUp = (value: boolean) => {
    showSkateRef.current = value
    setShowSkatePaymentFollowUpState(value)
  }

  const personalizationPromptAttempted = useRef(false)
  const inboxPromptAttempted = useRef(false)
  const pushRegistrationAttempted = useRef(false)

  /* ----------------------------------------------------------- sync helpers */

  const syncSkatePaymentFollowUpPrompt = () => {
    const auth = useAuthStore.getState()
    if (!auth.isAuthenticated || !auth.userProfile) {
      setShowSkatePaymentFollowUp(false)
      setActiveSkatePaymentFollowUp(null)
      return
    }
    const next = auth.getAwaitingSkatePaymentFollowUp()
    if (next) {
      setActiveSkatePaymentFollowUp(next)
      setShowSkatePaymentFollowUp(true)
    } else {
      setShowSkatePaymentFollowUp(false)
      setActiveSkatePaymentFollowUp(null)
    }
  }

  const onSkatePaymentFollowUpResolved = () => {
    setShowSkatePaymentFollowUp(false)
    setActiveSkatePaymentFollowUp(null)
    setTimeout(() => syncRef.current.syncSkatePaymentFollowUpPrompt(), 300)
  }

  const syncPersonalizationPrompt = () => {
    if (personalizationPromptAttempted.current) return
    if (showSkateRef.current) return
    const auth = useAuthStore.getState()
    if (!auth.isAuthenticated || !auth.userProfile) return
    if (AUTH_ROUTE_NAMES.has(routeNameRef.current as string)) return
    const personalization = usePersonalizationStore.getState()
    if (!personalization.needsPrompt) return

    personalizationPromptAttempted.current = true
    personalization.openChecklist()
  }

  const onPersonalizationSaved = () => {
    usePersonalizationStore.getState().closeChecklist()
  }

  const syncAdminInboxPrompt = async () => {
    if (inboxPromptAttempted.current) return
    if (showSkateRef.current) return
    if (usePersonalizationStore.getState().checklistOpen) return
    const auth = useAuthStore.getState()
    if (!auth.isAuthenticated || !auth.userProfile) return
    if (AUTH_ROUTE_NAMES.has(routeNameRef.current as string)) return
    if (!auth.isInboxReviewer) return
    const inbox = useAdminInboxStore.getState()
    if (inbox.dismissedThisSession) return

    inboxPromptAttempted.current = true
    await inbox.refresh()
    useAdminInboxStore.getState().maybeOpen()
  }

  // Ask for push permission on first open rather than making the user dig through
  // Profile settings. initializeAndRegister() reads the real OS state itself: it
  // registers silently when already granted and bails out when explicitly denied,
  // so this only surfaces a dialog for users who have never been asked.
  // On web the notification store's initializeAndRegister() is a no-op.
  const syncPushRegistration = () => {
    if (pushRegistrationAttempted.current) return
    const auth = useAuthStore.getState()
    if (auth.loading) return
    if (!auth.isAuthenticated || !auth.userProfile) return
    if (AUTH_ROUTE_NAMES.has(routeNameRef.current as string)) return
    // Wait for any in-app modal to clear so the OS dialog doesn't stack on top
    if (showSkateRef.current) return
    if (usePersonalizationStore.getState().checklistOpen) return
    if (useAdminInboxStore.getState().modalOpen) return

    pushRegistrationAttempted.current = true

    useNotificationStore
      .getState()
      .initializeAndRegister(auth.user!.uid)
      .catch((error) => console.log('Push registration error:', error))
  }

  const syncAppDownloadPrompt = () => {
    if (isEmbedRef.current) {
      setShowAppDownloadPrompt(false)
      return
    }
    const auth = useAuthStore.getState()
    if (auth.loading) {
      return
    }
    if (auth.isAuthenticated) {
      setShowAppDownloadPrompt(false)
      return
    }
    if (isAppDownloadPromptDismissed() || isOpenedFromApp()) {
      setShowAppDownloadPrompt(false)
      return
    }
    if (!isMobileWebViewport()) {
      setShowAppDownloadPrompt(false)
      return
    }
    setShowAppDownloadPrompt(true)
  }

  const onAppDownloadPromptDismiss = () => {
    dismissAppDownloadPromptForever()
    setShowAppDownloadPrompt(false)
  }

  const openNewsletterArchive = async () => {
    await menuController.close()
    setNewsletterArchiveOpen(true)
  }

  // Effects and timers call the latest helpers through this ref.
  const syncRef = useRef({
    syncSkatePaymentFollowUpPrompt,
    syncPersonalizationPrompt,
    syncAdminInboxPrompt,
    syncPushRegistration,
    syncAppDownloadPrompt
  })
  syncRef.current = {
    syncSkatePaymentFollowUpPrompt,
    syncPersonalizationPrompt,
    syncAdminInboxPrompt,
    syncPushRegistration,
    syncAppDownloadPrompt
  }

  /* ---------------------------------------------------------------- effects */

  // Embed shell: chrome-less, light-only pages for partner iframes.
  useEffect(() => {
    document.documentElement.classList.toggle('embed-shell', isEmbedRoute)
    document.body?.classList.toggle('embed-shell', isEmbedRoute)
    useThemeStore.getState().applyTheme()
  }, [isEmbedRoute])

  // Analytics screen view on every navigation (legacy router.afterEach).
  const lastLoggedKey = useRef<string | null>(null)
  useEffect(() => {
    if (lastLoggedKey.current === location.key) return
    lastLoggedKey.current = location.key
    const screenName = typeof route.name === 'string' ? route.name : route.path
    void useAnalyticsStore.getState().logScreenView(screenName, screenName, route.fullPath)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // Mount: league portals, cities, newsletters, analytics, first prompts.
  const mounted = useRef(false)
  useEffect(() => {
    if (mounted.current) return
    mounted.current = true
    useLeaguePortalsStore.getState().subscribe()
    void (async () => {
      useCityStore.getState().loadCities()
      useNewslettersStore.getState().subscribePublished()

      // Initialize Firebase Analytics
      const analytics = useAnalyticsStore.getState()
      await analytics.initialize()

      // Set user ID if already logged in
      const uid = useAuthStore.getState().user?.uid
      if (uid) {
        await useAnalyticsStore.getState().setUserId(uid)
      }

      const s = syncRef.current
      s.syncPersonalizationPrompt()
      s.syncAdminInboxPrompt()
      s.syncAppDownloadPrompt()
      s.syncPushRegistration()
    })()
  }, [])

  // Skate chats: live chat list, memberships, blocks and report count for the current city.
  useEffect(() => {
    useChatStore.getState().start({ uid: userUid || null, cityId: chatCityId || null, isCityModerator: isChatModerator })
  }, [userUid, chatCityId, isChatModerator])

  // Skate payment follow-ups on the profile changed.
  useEffect(() => {
    syncRef.current.syncSkatePaymentFollowUpPrompt()
  }, [skatePaymentFollowUps])

  // Auth state flipped.
  const prevAuthenticated = useRef(isAuthenticated)
  useEffect(() => {
    if (prevAuthenticated.current === isAuthenticated) return
    prevAuthenticated.current = isAuthenticated
    const s = syncRef.current
    if (isAuthenticated) {
      s.syncSkatePaymentFollowUpPrompt()
      s.syncPersonalizationPrompt()
      s.syncAdminInboxPrompt()
      s.syncPushRegistration()
    } else {
      setShowSkatePaymentFollowUp(false)
      setActiveSkatePaymentFollowUp(null)
      usePersonalizationStore.getState().closeChecklist()
      useAdminInboxStore.getState().close()
      inboxPromptAttempted.current = false
      // Let the next account on this device get its own prompt
      pushRegistrationAttempted.current = false
    }
  }, [isAuthenticated])

  // Personalization or route changed.
  useEffect(() => {
    syncRef.current.syncPersonalizationPrompt()
    syncRef.current.syncAdminInboxPrompt()
  }, [hubPersonalization, route.name])

  // Skate follow-up closed: queue the next prompts.
  useEffect(() => {
    if (!showSkatePaymentFollowUp) {
      syncRef.current.syncPersonalizationPrompt()
      syncRef.current.syncAdminInboxPrompt()
      syncRef.current.syncPushRegistration()
    }
  }, [showSkatePaymentFollowUp])

  // Auth can resolve after the app mounts (cold start, deep link), so re-check as
  // loading settles, the profile arrives, and the user leaves any auth route.
  useEffect(() => {
    syncRef.current.syncPushRegistration()
  }, [authLoading, isAuthenticated, userProfileId, route.name])

  // Queue behind the personalization checklist rather than stacking on it
  useEffect(() => {
    if (!checklistOpen) {
      syncRef.current.syncAdminInboxPrompt()
      syncRef.current.syncPushRegistration()
    }
  }, [checklistOpen])

  useEffect(() => {
    syncRef.current.syncAppDownloadPrompt()
  }, [authLoading, isAuthenticated])

  useEffect(() => {
    if (!inboxModalOpen) syncRef.current.syncPushRegistration()
  }, [inboxModalOpen])

  // Update analytics user ID when auth state changes
  const prevUid = useRef(userUid)
  useEffect(() => {
    if (prevUid.current === userUid) return
    prevUid.current = userUid
    void (async () => {
      const analytics = useAnalyticsStore.getState()
      if (analytics.isInitialized) {
        await analytics.setUserId(userUid || null)
      }
    })()
  }, [userUid])

  // Subscribe to schedules whenever city changes (real-time listener)
  const prevCityId = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const oldCityId = prevCityId.current
    prevCityId.current = currentCityId
    if (currentCityId && currentCityId !== oldCityId) {
      useGameStore.getState().subscribeToSchedules(currentCityId)
    }
  }, [currentCityId])

  return (
    <div className={`app-root${isEmbedRoute ? ' embed-shell' : ''}`}>
      {/* The document scrolls: top on a new navigation, restored on back/forward. */}
      <ScrollRestoration />

      {!isEmbedRoute && <NavigationProgress />}

      {!isEmbedRoute && (
        <a className="skip-to-content" href="#main-content" onClick={skipToContent}>
          Skip to main content
        </a>
      )}

      {!isEmbedRoute && <AppMenu onOpenNewsletterArchive={openNewsletterArchive} />}

      {/* Inert behind the open drawer, so Tab stays in the menu. */}
      <div className="app-main" id="main-content" inert={menuDrawerOpen}>
        {/* Keyed by path: Ionic created a fresh page per navigation, so pages
            reload when only a param changes (e.g. edit-user/A → edit-user/B). */}
        <Outlet key={location.pathname} />
      </div>

      {!isEmbedRoute && (
        <>
          <SkatePaymentFollowUpModal
            isOpen={showSkatePaymentFollowUp}
            followUp={activeSkatePaymentFollowUp}
            onDismiss={() => setShowSkatePaymentFollowUp(false)}
            onResolved={onSkatePaymentFollowUpResolved}
          />

          <NewsletterArchiveModal
            isOpen={newsletterArchiveOpen}
            issues={menuNewsletterIssues}
            onDismiss={() => setNewsletterArchiveOpen(false)}
          />

          <PersonalizationChecklistModal
            isOpen={checklistOpen}
            showUseDefaults={needsPrompt}
            onDismiss={() => usePersonalizationStore.getState().closeChecklist()}
            onSaved={onPersonalizationSaved}
          />

          <AdminActionInboxModal
            isOpen={inboxModalOpen}
            onDismiss={() => useAdminInboxStore.getState().dismiss()}
            onLater={() => useAdminInboxStore.getState().dismiss()}
          />

          <AppDownloadPromptModal
            isOpen={showAppDownloadPrompt}
            onDismiss={onAppDownloadPromptDismiss}
          />
        </>
      )}

      <OverlayHost />
    </div>
  )
}
